/**
 * MechaGuard - MapEditor (自定义地图编辑器)
 *
 * 交互模型：**点击格子连折线**。
 *   - 第一次点击定下起点；之后每次点击追加一个拐点
 *   - 与上一个点同行/同列时直接连；否则补一个直角肘部（先横后竖）
 *   - 出怪口不在网格内，而是根据「贴哪条边」自动生成在画面外一格
 *   - 最后一个点就是基地
 *
 * 为什么不做自由涂抹：
 *   现有数据模型里关卡就是一条折线（`level.path`），路径格是由折线
 *   推出来的。涂抹式编辑需要引入「障碍物」这个全新概念 —— 碰撞、
 *   寻路、渲染都要改，还要回头改三张内置地图。折线编辑则与
 *   createMap()/validateLevel() 完全对齐，改动被限制在新增文件里。
 *
 * 画布尺寸遵循 Renderer 里踩过的两个坑：
 *   1. 尺寸（含 DPR）没变时绝不重建缓冲区，否则白白清一次屏
 *   2. 按 DPR 放大后备缓冲区，否则高分屏上网格线发虚
 */

import { COLS, ROWS, collectPathTiles } from '../maps/MapData.js';
import { validateDraft, deriveLevelParams, SPAWN_EDGES } from '../maps/CustomMaps.js';

/** 网格配色（与 MapRenderer 的基调保持一致） */
const COLORS = {
  floor: '#0a1628',
  grid: 'rgba(80, 140, 200, 0.18)',
  buildable: 'rgba(60, 120, 180, 0.07)',
  lane: '#16304d',
  laneEdge: '#2f6ea8',
  spawn: '#ff9f43',
  base: '#00e0a4',
  point: '#7dffb0',
  pointLine: 'rgba(125, 255, 176, 0.75)',
  text: '#cfe6ff',
};

export class MapEditor {
  constructor() {
    this.canvas = null;
    this.ctx = null;

    /** 用户点击的网格内拐点（不含画面外的出怪口） */
    this._points = [];
    /** 撤销用的历史快照栈 */
    this._history = [];
    /** 出怪口贴哪条边 */
    this._edge = SPAWN_EDGES.LEFT;
    /** 正在编辑的地图 id；为 null 表示新建 */
    this._editingId = null;

    /** 当前格子的像素边长（绘制时计算） */
    this.cell = 32;
    this.cssW = 0;
    this.cssH = 0;
    this._hasSized = false;

    this._onSave = null;
    this._onClose = null;
  }

  /**
   * @param {{onSave:(draft:object, play:boolean)=>void, onClose:()=>void}} opts
   */
  init({ onSave, onClose } = {}) {
    this._onSave = onSave || null;
    this._onClose = onClose || null;

    this.canvas = document.getElementById('editor-canvas');
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');

    this._bindEvents();
  }

  /* ===================================================
   * 打开 / 关闭
   * =================================================== */

  /**
   * 打开编辑器。
   * @param {object|null} map 传入已有地图表示编辑，null 表示新建
   */
  open(map = null) {
    if (!this.canvas) return;

    this._editingId = map ? map.id : null;
    this._history = [];
    this._edge = SPAWN_EDGES.LEFT;

    if (map) {
      // 存档里 path[0] 是画面外的出怪口，其余才是玩家画的点
      this._points = map.path.slice(1).map(p => ({ x: p.x, y: p.y }));
      this._edge = map.path[0].x === -1 ? SPAWN_EDGES.LEFT : SPAWN_EDGES.RIGHT;

      // 极少数情况下存档只有出怪口一个点，退化成空草稿而不是崩掉
      if (this._points.length === 0) this._points = [];
    } else {
      this._points = [];
    }

    const title = document.getElementById('editor-title');
    if (title) title.textContent = map ? `编辑：${map.name}` : '新建地图';

    const nameInput = document.getElementById('editor-name');
    if (nameInput) nameInput.value = map ? map.name : '';

    // 画布尺寸依赖布局，必须等 overlay 显示出来之后再算
    this._hasSized = false;
    this._layout();
    this._refresh();
  }

  close() {
    if (this._onClose) this._onClose();
  }

  /* ===================================================
   * 事件
   * =================================================== */

  _bindEvents() {
    // 用 pointerdown 而不是 click：
    //
    // 1. 手机上没有 click 的 300ms 延迟，点下去即出拐点，手感跟手
    // 2. 之前这里在 touchstart 上做了 preventDefault 来阻止页面滚动 ——
    //    但那会连带**抑制浏览器合成的 click 事件**，手机上就完全点不动了。
    //    滚动交给 CSS 的 `touch-action: none` 处理即可，不需要 preventDefault。
    this.canvas.addEventListener('pointerdown', (e) => {
      const tile = this._hitTest(e);
      if (tile) this._appendWaypoint(tile.col, tile.row);
    });

    const on = (id, fn) => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('click', fn);
    };

    on('editor-edge-left', () => this._setEdge(SPAWN_EDGES.LEFT));
    on('editor-edge-right', () => this._setEdge(SPAWN_EDGES.RIGHT));
    on('editor-undo-btn', () => this.undo());
    on('editor-clear-btn', () => this.clear());
    on('editor-save-btn', () => this._emitSave(false));
    on('editor-play-btn', () => this._emitSave(true));
    on('editor-close-btn', () => this.close());

    const nameInput = document.getElementById('editor-name');
    if (nameInput) nameInput.addEventListener('input', () => this._refreshStats());

    // 窗口尺寸变化：只打标记，等下一次绘制前再应用（同 Renderer 的做法）
    window.addEventListener('resize', () => {
      if (!this._isOpen()) return;
      this._hasSized = false;
      this._layout();
      this._draw();
    });
  }

  _isOpen() {
    const root = document.getElementById('map-editor');
    return !!root && !root.classList.contains('hidden');
  }

  _hitTest(e) {
    const rect = this.canvas.getBoundingClientRect();
    const col = Math.floor((e.clientX - rect.left) / this.cell);
    const row = Math.floor((e.clientY - rect.top) / this.cell);
    if (col < 0 || col >= COLS || row < 0 || row >= ROWS) return null;
    return { col, row };
  }

  /**
   * 压入一份快照。
   *
   * 快照必须同时包含**拐点和出怪口方向** —— 只存拐点的话，
   * 撤销一次「切换出怪口」会毫无反应（点没变，方向却回不去）。
   */
  _pushHistory() {
    this._history.push({
      points: this._points.map(p => ({ ...p })),
      edge: this._edge,
    });
    if (this._history.length > 200) this._history.shift();
  }

  _setEdge(edge) {
    if (this._edge === edge) return;
    this._pushHistory();
    this._edge = edge;
    this._refresh();
  }

  /* ===================================================
   * 编辑操作
   * =================================================== */

  _appendWaypoint(col, row) {
    const points = this._points;
    const last = points[points.length - 1];

    // 原地重复点击没有意义，忽略掉以免污染撤销栈
    if (last && last.x === col && last.y === row) return;

    this._pushHistory();

    if (!last) {
      points.push({ x: col, y: row });
    } else if (last.x === col || last.y === row) {
      // 同行或同列：直接连一段
      points.push({ x: col, y: row });
    } else {
      // 需要拐一次直角。固定「先横后竖」，结果可预测而不是看运气
      points.push({ x: col, y: last.y });
      points.push({ x: col, y: row });
    }

    this._refresh();
  }

  undo() {
    const prev = this._history.pop();
    if (!prev) return;

    this._points = prev.points;
    this._edge = prev.edge;
    this._refresh();
  }

  clear() {
    if (this._points.length === 0) return;
    this._pushHistory();
    this._points = [];
    this._refresh();
  }

  /* ===================================================
   * 草稿 → 关卡路径
   * =================================================== */

  /** 出怪口坐标：贴在选定边的画面外一格，行号跟随第一个拐点 */
  _spawnPoint() {
    if (this._points.length === 0) return null;
    const x = this._edge === SPAWN_EDGES.LEFT ? -1 : COLS;
    return { x, y: this._points[0].y };
  }

  /** 完整折线：出怪口 + 玩家画的点 */
  _fullPath() {
    const spawn = this._spawnPoint();
    if (!spawn) return [];
    return [spawn, ...this._points.map(p => ({ x: p.x, y: p.y }))];
  }

  /** 当前草稿（供保存与校验） */
  getDraft() {
    const nameInput = document.getElementById('editor-name');
    return {
      id: this._editingId,
      name: nameInput ? nameInput.value : '',
      path: this._fullPath(),
      edge: this._edge,
    };
  }

  _emitSave(play) {
    const draft = this.getDraft();
    if (this._onSave) this._onSave(draft, play);
  }

  /* ===================================================
   * 版面
   * =================================================== */

  /**
   * 计算网格绘制尺寸并同步后备缓冲区。
   *
   * 尺寸（含 DPR）没变时直接返回 —— 给 canvas.width 赋值会清空画布
   * 并重新分配显存，是最容易踩的性能坑。
   */
  _layout() {
    const canvas = this.canvas;
    const wrap = canvas.parentElement;
    if (!wrap) return;

    const availW = Math.max(240, wrap.clientWidth || 320);
    // 高度上给工具栏留空间，同时不超过屏幕的 45%
    const availH = Math.max(
      140,
      Math.min(window.innerHeight * 0.45, (availW * ROWS) / COLS)
    );

    const cell = Math.max(12, Math.floor(Math.min(availW / COLS, availH / ROWS)));
    const w = cell * COLS;
    const h = cell * ROWS;

    const dpr = window.devicePixelRatio || 1;
    const bw = Math.round(w * dpr);
    const bh = Math.round(h * dpr);

    this.cell = cell;
    this.cssW = w;
    this.cssH = h;

    if (this._hasSized && bw === canvas.width && bh === canvas.height) return;

    this._hasSized = true;
    canvas.width = bw;
    canvas.height = bh;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /* ===================================================
   * 绘制
   * =================================================== */

  _refresh() {
    this._layout();
    this._draw();
    this._refreshStats();
    this._refreshToolState();
  }

  /** 只更新面板文字，不重绘网格 */
  _refreshStats() {
    const draft = this.getDraft();
    const check = validateDraft(draft);
    const params = deriveLevelParams(draft.path);

    const statsEl = document.getElementById('editor-stats');
    if (statsEl) {
      statsEl.textContent =
        `通道 ${params.tileLength} 格　💰 ${params.startGold}　`
        + `❤️ ${params.startLives}　★ ${params.difficulty}`;
    }

    const errorsEl = document.getElementById('editor-errors');
    if (errorsEl) {
      errorsEl.innerHTML = '';

      if (this._points.length === 0) {
        // 全新空白草稿：给操作指引，而不是甩一条「缺少拐点」的错误 ——
        // 那对第一次打开编辑器的人来说毫无帮助。
        errorsEl.classList.add('is-ok');
        errorsEl.textContent =
          '在网格上点击，画出敌人的行进路线（第一次点击是起点，最后一点是基地）';
      } else if (check.ok) {
        errorsEl.classList.add('is-ok');
        errorsEl.textContent = '✓ 路线合法，可以保存';
      } else {
        errorsEl.classList.remove('is-ok');
        // 一次只显示第一条，避免手机上文字刷屏
        errorsEl.textContent = `✕ ${check.errors[0]}`
          + (check.errors.length > 1 ? `（共 ${check.errors.length} 项问题）` : '');
      }
    }

    const saveBtn = document.getElementById('editor-save-btn');
    if (saveBtn) saveBtn.disabled = !check.ok;

    const playBtn = document.getElementById('editor-play-btn');
    if (playBtn) playBtn.disabled = !check.ok;
  }

  /** 出怪口方向按钮的选中态 */
  _refreshToolState() {
    const left = document.getElementById('editor-edge-left');
    const right = document.getElementById('editor-edge-right');
    if (left) left.classList.toggle('is-active', this._edge === SPAWN_EDGES.LEFT);
    if (right) right.classList.toggle('is-active', this._edge === SPAWN_EDGES.RIGHT);

    const undoBtn = document.getElementById('editor-undo-btn');
    if (undoBtn) undoBtn.disabled = this._history.length === 0;

    const clearBtn = document.getElementById('editor-clear-btn');
    if (clearBtn) clearBtn.disabled = this._points.length === 0;
  }

  _draw() {
    const ctx = this.ctx;
    if (!ctx) return;

    const cell = this.cell;

    // ---- 底色 ----
    ctx.fillStyle = COLORS.floor;
    ctx.fillRect(0, 0, this.cssW, this.cssH);

    // ---- 可建造格 ----
    ctx.fillStyle = COLORS.buildable;
    ctx.fillRect(0, 0, this.cssW, this.cssH);

    // ---- 通道格 ----
    const path = this._fullPath();
    if (path.length >= 2) {
      const pathSet = collectPathTiles(path);
      ctx.fillStyle = COLORS.lane;

      for (const key of pathSet) {
        const [cs, rs] = key.split(',');
        const c = Number(cs);
        const r = Number(rs);
        if (c < 0 || c >= COLS || r < 0 || r >= ROWS) continue;
        ctx.fillRect(c * cell, r * cell, cell, cell);
      }

      // ---- 折线本身 ----
      ctx.save();
      ctx.strokeStyle = COLORS.pointLine;
      ctx.lineWidth = Math.max(1.5, cell * 0.08);
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      path.forEach((p, i) => {
        // 编辑器里 1 格 = cell 像素，拐点取格心
        const x = (p.x + 0.5) * cell;
        const y = (p.y + 0.5) * cell;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      ctx.restore();
    }

    // ---- 网格线 ----
    ctx.save();
    ctx.strokeStyle = COLORS.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let c = 1; c < COLS; c++) {
      const x = c * cell + 0.5;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, this.cssH);
    }
    for (let r = 1; r < ROWS; r++) {
      const y = r * cell + 0.5;
      ctx.moveTo(0, y);
      ctx.lineTo(this.cssW, y);
    }
    ctx.stroke();
    ctx.restore();

    // ---- 出怪口 / 基地 ----
    const spawn = this._spawnPoint();
    if (spawn) {
      // 出怪口在画面外，标记画在它夹取后的第一格可见格上
      const col = Math.max(0, Math.min(COLS - 1, spawn.x));
      this._drawMarker(ctx, col, spawn.y, COLORS.spawn, '出');
    }

    if (this._points.length > 0) {
      const base = this._points[this._points.length - 1];
      this._drawMarker(ctx, base.x, base.y, COLORS.base, '基');
    }

    // ---- 拐点圆点与序号 ----
    ctx.save();
    ctx.font = `${Math.max(8, Math.floor(cell * 0.32))}px ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    this._points.forEach((p, i) => {
      const cx = (p.x + 0.5) * cell;
      const cy = (p.y + 0.5) * cell;
      const r = Math.max(2.5, cell * 0.12);

      ctx.fillStyle = COLORS.point;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();

      // 序号：点太多时省略，避免糊成一团
      if (this._points.length <= 12) {
        ctx.fillStyle = COLORS.floor;
        ctx.fillText(String(i + 1), cx, cy + 0.5);
      }
    });
    ctx.restore();

    // ---- 网格边框 ----
    ctx.save();
    ctx.strokeStyle = COLORS.laneEdge;
    ctx.lineWidth = 2;
    ctx.strokeRect(1, 1, this.cssW - 2, this.cssH - 2);
    ctx.restore();
  }

  _drawMarker(ctx, col, row, color, label) {
    const cell = this.cell;
    const x = col * cell;
    const y = row * cell;

    ctx.save();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.28;
    ctx.fillRect(x, y, cell, cell);
    ctx.globalAlpha = 1;

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(x + 1, y + 1, cell - 2, cell - 2);

    ctx.fillStyle = color;
    ctx.font = `${Math.max(9, Math.floor(cell * 0.4))}px ui-sans-serif, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + cell / 2, y + cell / 2);
    ctx.restore();
  }
}
